import React, {useState, useEffect} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import EditIcon from '@mui/icons-material/Edit';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import {uploadFileToS3} from '../../services/s3Service';
import {UPLOAD_TYPE} from '../../constants';
import AvatarDialog from './AvatarDialog';
import {updateUser, selectIsLoading, selectUser} from '../../redux/slices/user';
import PrimaryAppBar from './PrimaryAppBar';
import SecondaryAppBar from './SecondayAppBar';
import {BluePillButton} from '../../common/components/BluePillButton';
import {faUser} from '@fortawesome/pro-light-svg-icons';

import styles from '../css/Profile.module.css';
import baseStyles from '../../css/Base.module.css';

export default function Profile() {
  const dispatch = useDispatch();

  const isLoading = useSelector(selectIsLoading);
  const user = useSelector(selectUser);

  const [hasSetFormData, toggleHasSetFormData] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [avatarURL, setAvatarURL] = useState('');
  const [newAvatar, setNewAvatar] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [dialogMessage, setDialogMessage] = useState(null);
  const [isAvatarDialogOpen, toggleAvatarDialog] = useState(false);

  useEffect(() => {
    if (Object.keys(user).length > 0 && !hasSetFormData) {
      setFirstName(user?.firstName);
      setLastName(user?.lastName);
      setAvatarURL(user?.avatarURL);
      toggleHasSetFormData(true);
    }
  }, [user, hasSetFormData]);

  function handleAvatarDialogClose() {
    toggleAvatarDialog(false);
  }

  function handleFirstNameChanged(event) {
    setFirstName(event.target.value);
  }
  function handleLastNameChanged(event) {
    setLastName(event.target.value);
  }

  function hasNameChanged() {
    const formName = `${firstName}${lastName}`;
    const currentName = `${user?.firstName}${user?.lastName}`;

    return currentName !== formName;
  }

  function hasAvatarChanged() {
    return user?.avatarURL !== avatarURL || !!newAvatar;
  }

  function handleRemoveAvatar() {
    setAvatarURL('');
    setNewAvatar(null);
  }

  function isSubmitButtonEnabled() {
    return firstName && lastName && (hasNameChanged() || hasAvatarChanged());
  }

  function validateForm() {
    const errors = {};

    if (!firstName) errors.firstName = 'This field is required.';
    if (!lastName) errors.lastName = 'This field is required.';

    setFormErrors(errors);

    return Object.keys(errors).length === 0;
  }

  async function handleClickSubmit() {
    const isFormValid = validateForm();

    if (isFormValid) {
      try {
        let newAvatarURL = avatarURL;
        if (newAvatar) {
          const {blob, mimeType} = newAvatar;
          newAvatarURL = await uploadFileToS3(UPLOAD_TYPE.USER_AVATAR, blob, mimeType);
        }

        await dispatch(updateUser({firstName, lastName, avatarURL: newAvatarURL}));
      } catch (error) {
        setDialogMessage('An error occurred, try again.');
      }

      setDialogMessage('Your profile has been updated.');
    }
  }

  function renderDialog() {
    return (
      <Dialog open={!!dialogMessage} onClose={() => setDialogMessage(null)}>
        <DialogTitle>Profile</DialogTitle>
        <DialogContent>
          <DialogContentText>{dialogMessage}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogMessage(null)} color="primary" autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return <>
    <PrimaryAppBar />
    <SecondaryAppBar title="Profile"></SecondaryAppBar>

    {renderDialog()}
    <AvatarDialog
      isOpen={isAvatarDialogOpen}
      handleClose={handleAvatarDialogClose}
      avatarURL={avatarURL}
      setNewAvatar={setNewAvatar}
    />

    {isLoading ? (
      <div className={baseStyles['progress-container']}>
        <CircularProgress />
      </div>
    ) : (
      <div className="p-20">
        <Grid container spacing={3}>
          <Grid item xl={1} lg={2} md={3} sm={4} xs={6}>
            <TextField
              variant="standard"
              margin="dense"
              label="First name"
              onChange={handleFirstNameChanged}
              value={firstName}
              error={!!formErrors.firstName}
              helperText={formErrors.firstName}
              autoFocus
              fullWidth
            />
          </Grid>
          <Grid item xl={1} lg={2} md={3} sm={4} xs={6}>
            <TextField
              variant="standard"
              margin="dense"
              label="Last name"
              onChange={handleLastNameChanged}
              value={lastName}
              error={!!formErrors.lastName}
              helperText={formErrors.lastName}
              fullWidth
            />
          </Grid>
        </Grid>

        <Grid container direction="column" spacing={0} className="m-t-20">
          <Grid item xs={12}>
            <Typography variant="body1" className="p-5">
              Avatar
            </Typography>
            <Badge
              overlap="circular"
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              badgeContent={
                (!!newAvatar?.dataURL || !!avatarURL) && (
                  <EditIcon onClick={() => toggleAvatarDialog(true)} className={baseStyles['cursor-pointer']} />
                )
              }
            >
              {
                <Avatar
                  src={newAvatar?.dataURL || avatarURL}
                  className={[styles['avatar-large'], baseStyles['cursor-pointer']].join(' ')}
                  onClick={() => toggleAvatarDialog(true)}
                >
                  <FontAwesomeIcon icon={faUser} />
                </Avatar>
              }
            </Badge>
          </Grid>
          <Grid item xs={3} className="m-l-5 m-t-5">
            {(!!newAvatar?.dataURL || !!avatarURL) && (
              <Button size="small" onClick={handleRemoveAvatar}>
                Clear
              </Button>
            )}
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid item xl={2} lg={4} md={6} sm={8} xs={12} className={baseStyles['text-align-end']}>
            <BluePillButton variant="outlined" onClick={handleClickSubmit} disabled={!isSubmitButtonEnabled()}>
              SUBMIT
            </BluePillButton>
          </Grid>
        </Grid>
      </div>
    )}
  </>;
}
